// eslint-disable-next-line no-unused-vars
import { createSlice, createAsyncThunk, current } from '@reduxjs/toolkit';
import { requestStarted, requestFinished } from '~/stores/redux/common/loadingSlice';
import passiveApi from './passive.api';
import togglePublishedState from '~/api/togglePublishedState.api';
import { alertSwal } from '~/utils/helpers';
import { handleThunkException } from '~/services/thunk.service';

export const fetchPassiveApi = createAsyncThunk(
    'heroSaga/passive/fetchPassiveApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const data = await passiveApi.index();
            if (data) {
                dispatch(requestFinished());
                return data;
            }
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue([]);
        }
    },
);

export const togglePassivePublishedStateApi = createAsyncThunk(
    'heroSaga/passive/togglePassivePublishedStateApi',
    async (id, { dispatch, rejectWithValue }) => {
        try {
            await togglePublishedState(id);

            return id;
        } catch (error) {
            handleThunkException(error, dispatch);
            throw rejectWithValue(); //`throw` will dispatch RejectedAction. Ref: https://redux-toolkit.js.org/api/createAsyncThunk#promise-lifecycle-actions
        }
    },
);

export const createPassiveApi = createAsyncThunk(
    'heroSaga/hero/createPassiveApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const { data } = await passiveApi.create();

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const storePassiveApi = createAsyncThunk(
    'heroSaga/hero/storePassiveApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { navigate, values } = params;

        try {
            dispatch(requestStarted());
            const hero = await passiveApi.store(values);
            alertSwal('success', 'Passive created successfully!');

            return hero;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue(); //`return` will dispatch `RejectedWithValueAction`. Ref: https://redux-toolkit.js.org/api/createAsyncThunk#promise-lifecycle-actions
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const showPassiveApi = createAsyncThunk(
    'passiveSaga/passive/showPassiveApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { id, navigate } = params;

        try {
            dispatch(requestStarted());
            const { data } = await passiveApi.show(id);

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            throw rejectWithValue(); //`throw` will dispatch RejectedAction. Ref: https://redux-toolkit.js.org/api/createAsyncThunk#promise-lifecycle-actions
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const updatePassiveApi = createAsyncThunk(
    'heroSaga/passive/updatePassiveApi',
    async (params, { dispatch, getState, rejectWithValue }) => {
        const { navigate, id, values, setInitialValues } = params;
        const state = getState();

        try {
            dispatch(requestStarted());
            await passiveApi.update(id, values);
            alertSwal('success', 'Passive updated successfully!');
            setInitialValues(values);
            const { characterClasses } = state.heroSaga.heroMetaData;
            const updatedCharacterClass = characterClasses.find((item) => item.id === values.characterClassId);

            return { values, updatedCharacterClass };
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const deletePassiveApi = createAsyncThunk('heroSaga/hero/deletePassiveApi', async (params, { dispatch }) => {
    const { navigate, id } = params;

    try {
        await passiveApi.delete(id);

        return id;
    } catch (error) {
        handleThunkException(error, dispatch, navigate);
    }
});

export const passiveSlice = createSlice({
    name: 'heroSaga/passive',
    initialState: { rows: [], model: {}, editedModel: {} }, //model is initialValues that are used for formik.
    reducers: {
        clearEditedPassive(state) {
            state.editedModel = {};
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPassiveApi.fulfilled, (state, { payload }) => {
                return {
                    ...state,
                    rows: payload,
                };
            })
            .addCase(fetchPassiveApi.rejected, (state, { payload = [] }) => {
                state.rows = payload;
            });

        builder
            .addCase(togglePassivePublishedStateApi.fulfilled, (state, { payload }) => {
                const updateRow = state.rows.find((row) => row.id === payload);
                updateRow.isReady = !updateRow.isReady;
            })
            .addCase(togglePassivePublishedStateApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(createPassiveApi.fulfilled, (state, { payload }) => {
                state.model = payload.model;
            })
            .addCase(createPassiveApi.rejected, () => {
                //
            });

        builder
            .addCase(storePassiveApi.fulfilled, (state) => {
                return {
                    ...state,
                    rows: [], //clear all rows so that api fetchAll will be called again in index page.
                };
            })
            .addCase(storePassiveApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(showPassiveApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload.model;
            })
            .addCase(showPassiveApi.rejected, () => {
                //
            });

        builder
            .addCase(updatePassiveApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload; //this is to make Update component re-render so that the checkDuplicateName() will have the latest `initialName`.

                if (state.rows.length === 0) return;

                const updatedRow = state.rows.find((row) => row.id === payload.values.id);
                updatedRow.name = payload.values.name;
                updatedRow.characterClassName = payload.updatedCharacterClass.name;
            })
            .addCase(updatePassiveApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder.addCase(deletePassiveApi.fulfilled, (state, { payload }) => {
            return {
                ...state,
                rows: state.rows.filter(({ id }) => id !== payload),
            };
        });
    },
});

export const { clearEditedPassive } = passiveSlice.actions;
